import React, { FC, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { Popup } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Popup';
import Tracks from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/Tracks';
import { TrackMap } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/Tracks.types';
import { AudioTracks } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/AudioTracks/AudioTracks';
import { SoundQuality } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/SoundQuality/SoundQuality';
import { AudioTrack, TextTrack } from '@/scenarios/legacy-player/types/instance';

import { SubtitlesListContainer } from '../../subtitles/subtitles-list/container';
import { BaseControlProps } from '../../../types/controls';
import { SubtitlesConfig } from '../../../../Player';
import { usePlayerPopupButton } from '../../../hooks/skin/use-player-popup-button';

import styles from './styles.module.css';

interface Props extends BaseControlProps {
    audio: TrackMap;
    subtitles: SubtitlesConfig;
    currentAudioTrack?: AudioTrack;
    forceSubtitle?: boolean;
    className?: string;
    onSubtitleSizeSettingsClick: () => void;
    onSelectAudioTrack: (track: AudioTrack) => void;
    onSelectTextTrack: (track: TextTrack) => void;
}

export const AudioSettingPopupButton: FC<Props> = ({
    audio,
    currentAudioTrack,
    className,
    subtitles,
    forceSubtitle,
    onClick,
    onSubtitleSizeSettingsClick,
    onSelectAudioTrack,
    onSelectTextTrack,
}) => {
    const controlRef = useRef<HTMLDivElement>(null);
    const [isControlHovered, setIsControlHovered] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const { handleClick } = usePlayerPopupButton({
        isOpen,
        setIsOpen,
        onClick,
    });

    const tracksValue = useMemo(
        () => ({
            allAudioTracksMap: audio,
            currentAudioTrack: currentAudioTrack?.lang,
            watchParams: undefined,
        }),
        [audio, currentAudioTrack]
    );

    const intl = useIntl();

    return (
        <div
            onMouseEnter={() => setIsControlHovered(true)}
            onMouseLeave={() => setIsControlHovered(false)}
            ref={controlRef}
            className={classnames(styles.root, className)}
        >
            <button
                onClick={handleClick}
                className={styles.audioSettingsButton}
                aria-label={intl.formatMessage({
                    id: 'features.player.6dc9c1d2f8378cfa7ad04e2872a1b794',
                    defaultMessage: 'Audio and subtitles',
                    description: 'Лейбл кнопки "Аудио и субтитры" в плеере',
                })}
                aria-expanded={isOpen}
            />
            <Popup
                targetHovered={isControlHovered}
                lockTargetHover={setIsOpen}
                visible={isOpen}
                targetRef={controlRef}
                placement="top"
                enableSeamlessResize
            >
                <button
                    className={styles.subtitleSizeSettingsButton}
                    onClick={() => {
                        setIsOpen(false);
                        onSubtitleSizeSettingsClick();
                    }}
                >
                    <FormattedMessage
                        id="features.player.6f6cb72d544962fa333e2e34ce64f719"
                        defaultMessage="Size"
                        description="Текст в выборе размера субтитров в скине в фиче плеера"
                    />
                </button>
                <Tracks value={tracksValue}>
                    {audio.size > 0 ? (
                        <>
                            <SoundQuality onSelectAudioTrack={onSelectAudioTrack} />
                            <AudioTracks onSelectAudioTrack={onSelectAudioTrack} />
                        </>
                    ) : null}
                    <SubtitlesListContainer
                        subtitles={subtitles}
                        onSelectTextTrack={onSelectTextTrack}
                        forceSubtitle={forceSubtitle}
                    />
                </Tracks>
            </Popup>
        </div>
    );
};
